import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getEstateById, fetchHouses } from "../services/api";
import { formatUrl } from "../utils/config";
import { stripHtml } from "../utils/text";

const EstateHouses = () => {
  const { id } = useParams();
  const location = useLocation();
  const [estate, setEstate] = useState(location.state?.estate || null);
  const [loadingEstate, setLoadingEstate] = useState(!location.state?.estate);

  const [houses, setHouses] = useState([]);
  const [loadingHouses, setLoadingHouses] = useState(true);

  useEffect(() => {
    const loadEstate = async () => {
      if (!id) return;
      if (estate) return;
      setLoadingEstate(true);
      try {
        const res = await getEstateById(id);
        const data = res?.payload || res;
        setEstate(data);
      } catch (e) {
        console.error("Failed to load estate", e);
      } finally {
        setLoadingEstate(false);
      }
    };
    loadEstate();
  }, [id, estate]);

  useEffect(() => {
    const loadHouses = async () => {
      if (!id) return;
      setLoadingHouses(true);
      try {
        const res = await fetchHouses(0, 100, { estateId: id });
        const page = res?.payload?.page || [];
        setHouses(Array.isArray(page) ? page : []);
      } catch (e) {
        console.error("Failed to load houses for estate", e);
        setHouses([]);
      } finally {
        setLoadingHouses(false);
      }
    };
    loadHouses();
  }, [id]);

  return (
    <>
      <Helmet>
        <title>{estate?.name ? `${estate.name} - All Houses` : "Estate Houses"}</title>
        <meta name="description" content={estate?.description ? stripHtml(estate.description) : "All houses for estate"} />
      </Helmet>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{estate?.name || "Estate Houses"}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/estates">Estates</Link>
              </li>
              {estate?.name && <li>{estate.name}</li>}
              <li>All Houses</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          {loadingHouses ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : houses.length === 0 ? (
            <p className="text-muted">No houses found for this estate.</p>
          ) : (
            <div className="row gy-4">
              {houses.map((property) => (
                <div key={property.id} className="col-xl-4 col-md-6">
                  <div className="project-card style1-1 property-card-hover">
                    <div className="project-card-img">
                      <img
                        src={formatUrl(property?.thumbnail) || "/assets/img/main/default.jpg"}
                        alt={property?.title || `${property?.houseNumber} - ${property?.buildingType}`}
                        className="w-100"
                        onError={(e) => (e.currentTarget.src = "/assets/img/main/default.jpg")}
                      />
                    </div>
                    <div className="project-card-details" style={{ transition: "all 0.3s ease-in-out" }}>
                      <h4 className="box-title">
                        <Link
                          to={`/properties/${property.id}`}
                          state={{ property }}
                          style={{ color: "inherit", textDecoration: "none", transition: "color 0.3s ease-in-out" }}
                        >
                          {property?.houseNumber} - {property?.buildingType}
                        </Link>
                      </h4>
                      <p className="mb-2" style={{ transition: "color 0.3s ease-in-out" }}>
                        <i className="fas fa-map-marker-alt me-2" style={{ color: "inherit" }}></i>
                        {(property?.estate?.name || estate?.name) || "Estate"}
                        {property?.blockNumber ? `, Block ${property.blockNumber}` : ""}
                      </p>
                      <div className="property-features mb-3 d-flex flex-wrap gap-2" style={{ transition: "color 0.3s ease-in-out" }}>
                        {typeof property?.bedRooms === "number" && (
                          <span className="me-3">
                            <i className="fas fa-bed me-1"></i> {property.bedRooms} Beds
                          </span>
                        )}
                        {typeof property?.toilets === "number" && (
                          <span className="me-3">
                            <i className="fas fa-toilet me-1"></i> {property.toilets} Baths
                          </span>
                        )}
                        {property?.livingRoom > 0 && (
                          <span className="me-3">
                            <i className="fas fa-couch me-1"></i> {property.livingRoom} Living
                          </span>
                        )}
                        {property?.kitchen > 0 && (
                          <span className="me-3">
                            <i className="fas fa-utensils me-1"></i> {property.kitchen} Kitchen
                          </span>
                        )}
                        {property?.dianning > 0 && (
                          <span>
                            <i className="fas fa-chair me-1"></i> {property.dianning} Dining
                          </span>
                        )}
                      </div>
                      <Link to={`/properties/${property.id}`} state={{ property }} className="th-btn style9" style={{ transition: "all 0.3s ease-in-out" }}>
                        View Details <i className="fas fa-arrow-right ms-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default EstateHouses;
